import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Package, FileText, TrendingUp, Plus, ArrowRight } from "lucide-react";

export default function Dashboard() {
  const [stats, setStats] = useState({ customers: 0, products: 0, quotes: 0, sent: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/customers"),
      api.get("/products"),
      api.get("/quotes"),
    ]).then(([c, p, q]) => {
      setStats({
        customers: c.data.length,
        products: p.data.length,
        quotes: q.data.length,
        sent: q.data.filter((x) => x.status === "sent").length,
      });
      setRecent(q.data.slice(0, 6));
    }).catch(() => {});
  }, []);

  const cards = [
    { label: "Müşteriler", value: stats.customers, icon: Users, to: "/musteriler" },
    { label: "Ürünler", value: stats.products, icon: Package, to: "/urunler" },
    { label: "Toplam Teklif", value: stats.quotes, icon: FileText, to: "/teklifler" },
    { label: "Gönderilen Teklif", value: stats.sent, icon: TrendingUp, to: "/teklifler" },
  ];

  return (
    <div>
      <PageHeader
        title="Panel"
        subtitle="Konzade CRM'e hoş geldiniz."
        action={
          <Link to="/teklifler/yeni">
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="dashboard-new-quote">
              <Plus className="w-4 h-4 mr-2" /> Yeni Teklif
            </Button>
          </Link>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <Link to={c.to} key={c.label}>
            <Card className="p-5 border-slate-200 hover:-translate-y-1 hover:shadow-md transition-all duration-200 h-full" data-testid={`stat-${c.label}`}>
              <div className="flex items-center justify-between mb-4">
                <span className="w-10 h-10 rounded-md bg-black text-white flex items-center justify-center">
                  <c.icon className="w-5 h-5" />
                </span>
              </div>
              <p className="text-3xl font-heading font-black">{c.value}</p>
              <p className="text-sm text-slate-500 mt-1">{c.label}</p>
            </Card>
          </Link>
        ))}
      </div>

      <Card className="border-slate-200">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="font-heading font-bold text-lg">Son Teklifler</h3>
          <Link to="/teklifler" className="text-sm text-rose-600 font-medium flex items-center gap-1 hover:gap-2 transition-all">
            Tümünü Gör <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="divide-y divide-gray-100">
          {recent.length === 0 && <p className="p-6 text-sm text-slate-500">Henüz teklif oluşturulmadı.</p>}
          {recent.map((q) => (
            <Link to={`/teklifler/${q.id}`} key={q.id} className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{q.quote_number} · {q.customer_name}</p>
                <p className="text-xs text-slate-500">{(q.created_at || "").slice(0, 10)}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-heading font-bold text-sm">{money(q.grand_total, q.currency)}</span>
                <Badge variant={q.status === "sent" ? "default" : "secondary"} className={q.status === "sent" ? "bg-rose-600" : ""}>
                  {q.status === "sent" ? "Gönderildi" : "Taslak"}
                </Badge>
              </div>
            </Link>
          ))}
        </div>
      </Card>
    </div>
  );
}
